import { ReactNode, useEffect, useRef, useState } from "react";
import { DsaBoardDto, DsaContentDto, DsaPlaylistDto, DsaScheduleDto, DsaScreenAddDto, DsaScreenDto } from "../../generated/dashboard-client"
import { Button, Drawer, Dropdown, Input } from "antd";
import DSAApi from "../../api/DSAApi";
import { getRendererThumbPathWithPage, getS3Url } from "../../util/utils";
import MediaThumb from "./MediaThumb";
import { useRecoilState, useRecoilValue } from "recoil";
import { locationsState, memberState, mobileState } from "../../state/atoms";
import {
  DeleteOutlined, DownOutlined, EditOutlined, EyeOutlined, LoadingOutlined, LockOutlined, MoreOutlined, PauseOutlined, SettingOutlined,
  ToolOutlined, UnlockOutlined
} from "@ant-design/icons";
import StartPlayingIcon from "../icons/StartPlayingIcon";
import RebootScreenIcon from "../icons/RebootScreenIcon";
import { goToDesign } from "../../service/PosService";
import useFocus from "../../hooks/useFocus";
import { useOnClickOutside } from "usehooks-ts";
import { useNavigate } from "react-router-dom";
import { env } from "../../env/env";
import React from "react";
import DsaContentPlaceholderIcon from "../icons/DsaContentPlaceholderIcon";
import "./ScreenCard.scss";
import { ScreenView } from "../pages/DSAManage";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import { getPlaylistImage } from "./PlaylistCard";
import moment from "moment";
import WiFiSignalOff from "../icons/WiFiSignalOff";
import WiFiSignal0 from "../icons/WiFiSignal0";
import WiFiSignal1 from "../icons/WiFiSignal1";
import WiFiSignal2 from "../icons/WiFiSignal2";
import WiFiSignal3 from "../icons/WiFiSignal3";
import WiFiSignal4 from "../icons/WiFiSignal4";
import CheckCircleFilled from "../icons/CheckCircleFilled";
import CloseCircle from "../icons/CloseCircle";
import LocationIconRound from "../icons/LocationIconRound";

interface ScreenCardProps {
  it: DsaScreenDto;
  screens: DsaScreenDto[];
  setScreens: (screens: ScreenView[]) => void;
  boards: DsaBoardDto[];
  playlists: DsaPlaylistDto[];
  schedules: DsaScheduleDto[];
  uploads: DisplayUploadDto[];
  lockedForUser: boolean | undefined;
  setActive: (it: DsaScreenDto) => void;
  setEditContentModal: (visible: boolean) => void;
  openPreviewModal: (it: DsaScreenDto) => void;
  rebootScreen: (it: DsaScreenDto) => void;
  showDiagnostics: (it: DsaScreenDto) => void;
  removeScreen: (it: DsaScreenDto) => void;
  openManageScreenModal: (it: DsaScreenDto) => void;
  withTooltip: (tooltip: string, element: ReactNode) => ReactNode;
}

export default function ScreenCard({
  it,
  screens,
  setScreens,
  boards,
  playlists,
  schedules,
  uploads,
  lockedForUser,
  setActive,
  setEditContentModal,
  openPreviewModal,
  rebootScreen,
  removeScreen,
  showDiagnostics,
  openManageScreenModal,
  withTooltip
}: ScreenCardProps) {
  const member = useRecoilValue(memberState);
  const mobile = useRecoilValue(mobileState);
  const [locations] = useRecoilState(locationsState);
  const [playLoading, setPlayLoading] = useState(false);

  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [renameInputRef, setRenameInputFocus] = useFocus();

  const [isMenuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);

  useEffect(() => {
    setName(it.name ?? "");
  }, [it]);

  function hideDropdown() {
    setMenuVisible(false);
  }

  function hideMobileMenu() {
    setMobileMenuVisible(false);
  }

  const handleClickOutside = (ev: MouseEvent) => {
    hideDropdown();
  }

  useOnClickOutside(menuRef, (ev) => handleClickOutside(ev as MouseEvent));

  function startRenaming() {
    if (lockedForUser) return;
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  async function rename() {
    setRenaming(false);
    if (!name || name === it.name) {
      setName(it.name!);
      return;
    }
    try {
      await DSAApi.updateScreen(it.serial!, {
        name: name,
        locationId: it.locationId,
        placement: it.placement,
        timezone: it.timezone,
        rotation: it.rotation,
        scheduledReboot: it.scheduledReboot,
        restartDay: it.restartDay,
        restartTime: it.restartTime,
      } as DsaScreenAddDto);
      screens.find(sc => sc.serial! === it.serial!)!.name = name;
      setScreens(screens);
    } catch (e) {
      console.error(e);
    }
  }

  function getContentName(c: DsaContentDto) {
    if (c.type === 'BOARD') {
      return boards.find(it => it.id === c.sourceId)?.name;
    } else if (c.type === 'PLAYLIST') {
      return playlists.find(it => it.id === c.sourceId)?.name;
    } else if (c.type === 'SCHEDULE') {
      return schedules.find(it => it.id === c.sourceId)?.name;
    }
  }

  function getContentThumbnail(screen: DsaScreenDto | undefined) {
    if (!screen) return;
    const c = screen.content;
    const markLoaded = (ev: any) => {
      (ev.target as HTMLElement).parentElement!.classList.add('thumb-loaded');
    };
    if (c) {
      if (c.type === 'BOARD') {
        const cBoard = boards.find(b => b.id === c.sourceId)!;
        if (cBoard) {
          const cUpload = cBoard.type === 'UPLOAD' ? uploads.find(u => u.id === cBoard.uploadId) : null;
          return <MediaThumb
            src={cBoard.type === 'UPLOAD' ? getS3Url(cUpload ? cUpload.path : '') : getRendererThumbPathWithPage(cBoard.designId!, cBoard.page!)}
            alt={screen?.name!}
            onClick={cBoard.type === 'UPLOAD' ? () => onUploadThumbClick() : () => onDesignImageClick(cBoard.designId!)}
            onLoad={markLoaded}
          />;
        }
      } else if (c.type === 'PLAYLIST') {
        const cPlaylist = playlists.find(b => b.id === c.sourceId)!;
        if (cPlaylist) {
          return <MediaThumb
            src={getPlaylistImage(cPlaylist, uploads)}
            alt={screen?.name!}
            onClick={() => onPlaylistThumbClick(cPlaylist.id!)}
            onLoad={markLoaded}
          />;
        }
      } else if (c.type === 'SCHEDULE') {
        const cSchedule = schedules.find(s => s.id === c.sourceId)!;
        if (cSchedule) {
          const firstEvent = cSchedule.events?.sort((a, b) => {
            const fullADate = a.startDate! + parseInt(moment(a.startTime, 'HH:mm').format('X'));
            const fullBDate = b.startDate! + parseInt(moment(b.startTime, 'HH:mm').format('X'));
            return fullADate - fullBDate;
          })![0]!;

          return <MediaThumb
            src={firstEvent.contentDesign}
            alt={screen?.name!}
            onClick={() => onScheduleThumbClick(cSchedule.id!)}
            onLoad={markLoaded}
          />;
        }
      }
    }
    return <DsaContentPlaceholderIcon />
  }

  function onDesignImageClick(designId: string) {
    if (lockedForUser) return;
    goToDesign(designId);
  }

  function onPlaylistThumbClick(playlistId: string) {
    if (lockedForUser) return;
    sessionStorage.setItem('edit-playlist', playlistId);
    navigate(`${env.dashboardPath}/dsa/playlists`);
  }

  function onUploadThumbClick() {
    navigate(`${env.dashboardPath}/dsa/uploads`);
  }

  function onScheduleThumbClick(scheduleId: string) {
    if (lockedForUser) return;
    sessionStorage.setItem('edit-schedule', scheduleId);
    navigate(`${env.dashboardPath}/dsa/schedules`);
  }

  function getContentLabel(c: DsaContentDto) {
    if (c.type === 'BOARD') {
      const cBoard = boards.find(b => b.id === c.sourceId)!;
      if (cBoard && cBoard.type === 'UPLOAD') {
        return 'UPLOAD';
      } else {
        return 'DESIGN'
      }
    }
    return c.type;
  }

  function linkToScreen(screen: DsaScreenDto) {
    if (lockedForUser) return;
    setActive(screen);
    setEditContentModal(true);
  }

  async function togglePlayable(screen: DsaScreenDto, playable: boolean) {
    if (!screen.serial) return;
    if (!it.online) return;
    if (!screen.content) {
      linkToScreen(screen);
      return;
    }
    setPlayLoading(true);
    const resp = await DSAApi.updateScreenContent(screen.serial, { playable });
    const newScreen = resp.data;
    screens.find(it => it.serial === newScreen.serial)!.content = newScreen.content;
    setScreens([...screens]);
    setPlayLoading(false);
  }

  async function onDropdownTogglePlayClick(screen: DsaScreenDto, playable: boolean) {
    if (!it.online) return;
    hideDropdown();
    togglePlayable(screen, playable);
  }

  function onEditClick(screen: DsaScreenDto) {
    hideDropdown();
    hideMobileMenu();
    linkToScreen(screen);
  }

  function onPreviewClick(screen: DsaScreenDto) {
    hideDropdown();
    hideMobileMenu();
    openPreviewModal(screen);
  }

  function onManageScreenClick(screen: DsaScreenDto) {
    hideDropdown();
    hideMobileMenu();
    openManageScreenModal(screen);
  }

  function toggleLockScreen(screen: DsaScreenDto) {
    hideDropdown();
    hideMobileMenu();
    DSAApi.toggleLockScreen(screen.serial!).then(resp => {
      console.log(`screen ${screen.name} is now ${resp.data.locked ? 'locked' : 'unlocked'}`);
      screens.find(it => it.serial === screen.serial)!.locked = resp.data.locked;
      setScreens([...screens]);
    })
  }

  function onRebootScreenClick(screen: DsaScreenDto) {
    if (!it.online) return;
    hideDropdown();
    hideMobileMenu();
    rebootScreen(screen);
  }

  function onShowDiagnosticsClick(screen: DsaScreenDto) {
    if (!it.online) return;
    hideDropdown();
    hideMobileMenu();
    showDiagnostics(screen);
  }

  function onRemoveScreenClick(screen: DsaScreenDto) {
    hideDropdown();
    hideMobileMenu();
    removeScreen(screen);
  }

  const screenCardMenu = (
    <ul ref={menuRef}>
      {it.online && <li onClick={() => onDropdownTogglePlayClick(it, !it.content?.playable)}>
        {it.content?.playable ?
          <><div><PauseOutlined /></div>Pause Playing</> :
          <><div className="custom-icon"><StartPlayingIcon /></div>Start Playing</>}
      </li>}
      {!it.online && withTooltip('Screen must be online to access this feature', <li className="offline">
        {it.content?.playable ?
          <><div><PauseOutlined /></div>Pause Playing</> :
          <><div className="custom-icon"><StartPlayingIcon /></div>Start Playing</>}
      </li>)}
      {!lockedForUser && !mobile && <li onClick={() => onEditClick(it)}><EditOutlined />{it.content ? 'Edit' : 'Assign'} Content</li>}
      {it.content?.type !== 'SCHEDULE' && <li onClick={() => onPreviewClick(it)}><EyeOutlined />Preview</li>}
      {!lockedForUser && <li onClick={() => onManageScreenClick(it)}>
        <SettingOutlined />
        Manage Screen
      </li>}
      {member.owner && <li onClick={() => toggleLockScreen(it)}>
        {it.locked ?
          <><UnlockOutlined />Unlock Screen</> :
          <><LockOutlined />Lock Screen</>}
      </li>}
      {it.online && !lockedForUser && <li onClick={() => onRebootScreenClick(it)}>
        <div className="custom-icon reboot-icon"><RebootScreenIcon /></div>
        Reboot Screen
      </li>}
      {!it.online && !lockedForUser && withTooltip('Screen must be online to access this feature', <li className="offline">
        <div className="custom-icon reboot-icon"><RebootScreenIcon /></div>
        Reboot Screen
      </li>)}
      {it.online && !lockedForUser && <li onClick={() => onShowDiagnosticsClick(it)}>
        <div className="reboot-icon"><ToolOutlined /></div>
        Display Diagnostics
      </li>}
      {!it.online && !lockedForUser && withTooltip('Screen must be online to access this feature', !lockedForUser && <li className="offline">
        <div className="reboot-icon"><ToolOutlined /></div>
        Display Diagnostics
      </li>)}
      {!lockedForUser && <li onClick={() => onRemoveScreenClick(it)}><DeleteOutlined />Remove Screen</li>}
    </ul>
  )

  const getWifiIcon = (it: DsaScreenDto) => {
    if (it.wifiSignal === undefined || it.wifiSignal < 0) {
      return <WiFiSignalOff />;
    } else if (it.wifiSignal === 0) {
      return <WiFiSignal0 />;
    } else if (it.wifiSignal === 1) {
      return <WiFiSignal1 />;
    } else if (it.wifiSignal === 2) {
      return <WiFiSignal2 />;
    } else if (it.wifiSignal === 3) {
      return <WiFiSignal3 />;
    } else {
      return <WiFiSignal4 />;
    }
  }

  const hdmiIcon = (it: DsaScreenDto) => {
    if (it.hdmiConnected && it.online) {
      return <CheckCircleFilled />;
    } else {
      return <CloseCircle />;
    }
  }

  return (<>
    <span className="status-col">
      <span className={`status-indicator ${it.online ? "online" : ""}`}>
        <span className="status-text">
          <span>{it.online ? "online" : "offline"}</span>
          <span className="serial">{it.serial!}</span>
        </span>
      </span>
    </span>
    <span className="location-col">
      {locations.find(loc => loc.internalId === it.locationId)?.name}
    </span>
    <span className="screen-col">
      {!mobile && it.online && <div className="playpause-btn" onClick={() => togglePlayable(it, !it.content?.playable)}>
        {it.content && it.content?.playable ?
          withTooltip("Pause", <div className="icon">{playLoading ? <LoadingOutlined /> : <PauseOutlined />}</div>) :
          withTooltip("Play", <div className="icon">{playLoading ? <LoadingOutlined /> : <StartPlayingIcon />}</div>)}
      </div>}
      {!mobile && !it.online && <div className="playpause-btn offline">
        {it.content && it.content?.playable ?
          withTooltip("Screen must be online to access this feature", <div className="icon"><PauseOutlined /></div>) :
          withTooltip("Screen must be online to access this feature", <div className="icon"><StartPlayingIcon /></div>)}
      </div>}
      <div className={`thumbnail-container ${it.content === null ? 'no-content' : ''} ${lockedForUser ? 'locked' : ''}`}>
        <LoadingOutlined />
        {getContentThumbnail(it)}
      </div>
      <div className="naming-info">
        {!isRenaming && <div
          className={`edit-name ${lockedForUser ? 'naming-locked' : ''}`}
          onClick={startRenaming}
        >
          <h4>{name}</h4>
          <EditOutlined />
        </div>}
        {!lockedForUser && <div
          className={`editing-name ${isRenaming ? "show" : ""}`}
        >
          <Input
            ref={renameInputRef}
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            onBlur={rename}
          />
        </div>}
        {mobile && <div className="mobile-info">
          <div className={`online-indicator ${it.online ? 'online' : 'offline'}`} />
          <span>{locations.find(loc => loc.internalId === it.locationId)?.name}</span>
        </div>}
      </div>
    </span>
    <span className="content-col">
      {it.content && <div className={`currently-assigned ${lockedForUser ? 'locked' : ''}`}>
        {!lockedForUser && <span className="edit-content-btn" onClick={() => linkToScreen(it)}>
          <EditOutlined />
        </span>}
        <div>
          <span><b>{getContentLabel(it.content)}:</b></span><br />
          <span>{getContentName(it.content)}</span>
        </div>
      </div>}
      {!it.content && <section>
        <div className="none">
          <Button className={"secondary"} onClick={() => linkToScreen(it)}>+ Assign content</Button>
        </div>
      </section>}
    </span>
    <span className="hdmi-col">
      {hdmiIcon(it)}
    </span>
    <span className="wifi-col">
      {it.online &&
        <div>
          <span>{getWifiIcon(it)}</span>
          <span>{it.wifiSsid ?? 'WiFi Unavailable'}</span>
        </div>}
      {!it.online &&
        <div>
          <span><WiFiSignalOff /></span>
          <span>WiFi Unavailable</span>
        </div>}
    </span>
    {!member.owner && <span className="locked-col">
      {it.locked ?
        withTooltip("Locked by owner", <LockOutlined />) :
        <>&mdash;&nbsp;&mdash;</>}
    </span>}

    <span className="menu-col">
      {!mobile && withTooltip("More",
        <Dropdown
          visible={isMenuVisible}
          placement="bottomRight"
          arrow={false}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          overlayClassName="screen-edit-menu"
          overlay={screenCardMenu}
        >
          <div className="screen-edit-trigger">
            <MoreOutlined onClick={() => {
              setMenuVisible(!isMenuVisible)
            }} />
          </div>
        </Dropdown>)}

      {mobile && <>
        <MoreOutlined id={it.serial} onClick={() => {
          setMobileMenuVisible(!isMobileMenuVisible)
        }} />
        <Drawer
          placement={"bottom"}
          visible={isMobileMenuVisible}
          onClose={() => setMobileMenuVisible(false)}
          closeIcon={<DownOutlined />}
          className={"mobile-bottom-menu screen-edit-menu"}
          height={510}>
          <div className="mobile-menu-header">
            <div className="loc">
              <LocationIconRound />
              {locations.find(loc => loc.internalId === it.locationId)?.name}
            </div>
            <div className="name">{it.name!}</div>
            {it.content && <div className="assigned" onClick={() => linkToScreen(it)}>
              <EditOutlined />
              <div>
                <span><b>{getContentLabel(it.content)}:</b></span>
                <span>{getContentName(it.content)}</span>
              </div>
            </div>}
            <div className="statuses">
              <div className={`status-indicator ${it.online ? "online" : ""}`}>{it.online ? "online" : "offline"}</div>
              <div className="hdmi-indicator">{hdmiIcon(it)} HDMI</div>
              <div className="wifi-indicator">{getWifiIcon(it)}{it.wifiSsid ?? 'WiFi Unavailable'}</div>
            </div>
          </div>
          {screenCardMenu}
        </Drawer>
      </>}
    </span>
  </>)
}
